import { useMemo } from "react";
import {
  Avatar,
  Box,
  Typography,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
  gridClasses,
} from "@mui/x-data-grid";

const AVATAR_COLORS = ["#6366f1", "#10b981", "#f79009", "#06AED4", "#9E77ED", "#F04438", "#2970FF"];

const DEFAULT_STATUS_COLORS = {
  Active: "success",
  Probation: "warning",
  "On Notice": "error",
  Approved: "success",
  Pending: "warning",
  Rejected: "error",
  Paid: "success",
  Present: "success",
  Late: "warning",
  Absent: "error",
  Draft: "secondary",
  "Pending Approval": "warning",
  Published: "success",
  Closed: "secondary",
};

export const gridShellSx = {
  width: "100%",
  bgcolor: "background.paper",
  borderRadius: "20px",
  boxShadow: "0px 5px 22px rgba(0, 0, 0, 0.04), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.03)",
  overflow: "hidden",
};

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function avatarColor(seed = "") {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function PersonAvatar({ name, size = 30, src }) {
  // With a valid `src` MUI shows the photo; otherwise it falls back to the
  // coloured initials (same avatar shown today).
  return (
    <Avatar src={src || undefined} sx={{ width: size, height: size, fontSize: 12, bgcolor: avatarColor(name), fontWeight: 600 }}>
      {initials(name)}
    </Avatar>
  );
}

// Small green "verified" badge shown next to a manager's name.
export function ManagerBadge({ size = 15 }) {
  return (
    <Box component="span" title="Manager" aria-label="Manager" sx={{ display: "inline-flex", flexShrink: 0, lineHeight: 0 }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="#10b981" />
        <path d="M8 12.4l2.6 2.6 5-5.6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Box>
  );
}

export function PersonCell({ name, subtitle, manager, avatar }) {
  if (!name) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <PersonAvatar name={name} src={avatar} />
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ lineHeight: 1.3 }}>
            {name}
          </Typography>
          {manager && <ManagerBadge />}
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.2 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export function TextCell({ value, fontWeight }) {
  return (
    <Typography variant="body2" color="text.primary" fontWeight={fontWeight} noWrap>
      {value ?? "—"}
    </Typography>
  );
}

export function GridStatusPill({ status, tone, colorMap = DEFAULT_STATUS_COLORS }) {
  const theme = useTheme();
  const colorKey = tone || colorMap[status] || "secondary";
  const paletteColor = theme.palette[colorKey] || theme.palette.secondary;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "12px",
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1,
        px: 1.25,
        py: 0.75,
        whiteSpace: "nowrap",
        bgcolor: alpha(paletteColor.main, 0.12),
        color: theme.palette.mode === "dark" ? paletteColor.main : paletteColor.dark,
      }}
    >
      {status}
    </Box>
  );
}

export function useDataGridSx({ cursor = "pointer", fillHeight = false } = {}) {
  const theme = useTheme();
  const headerBg = theme.palette.mode === "dark" ? theme.palette.grey[800] : "#F3F4F6";
  const hoverBg = theme.palette.mode === "dark"
    ? alpha(theme.palette.primary.main, 0.12)
    : theme.palette.primary.lighter || alpha(theme.palette.primary.main, 0.08);

  return {
    border: "none",
    ...(fillHeight ? {
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      [`& .${gridClasses.toolbarContainer}`]: {
        flexShrink: 0,
        overflowY: "hidden",
      },
      [`& .${gridClasses.main}`]: {
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      },
      [`& .${gridClasses.virtualScroller}`]: {
        overflow: "auto",
      },
      [`& .${gridClasses.footerContainer}`]: {
        flexShrink: 0,
      },
    } : {}),
    [`& .${gridClasses.columnHeaders}`]: {
      bgcolor: headerBg,
      borderBottom: "1px solid",
      borderColor: "divider",
      overflow: "hidden",
    },
    [`& .${gridClasses.columnHeadersInner}`]: {
      overflow: "hidden",
    },
    [`& .${gridClasses["columnHeadersInner--scrollable"]}`]: {
      overflow: "hidden",
    },
    [`& .${gridClasses.columnHeader}`]: {
      "&:focus, &:focus-within": { outline: "none" },
    },
    [`& .${gridClasses.columnHeaderTitle}`]: {
      textTransform: "uppercase",
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "0.5px",
      color: "text.secondary",
    },
    [`& .${gridClasses.cell}`]: {
      display: "flex",
      alignItems: "center",
      borderTop: "none",
      borderBottom: "1px solid",
      borderColor: "divider",
      py: 0,
      "&:focus, &:focus-within": { outline: "none" },
    },
    [`& .${gridClasses.row}`]: {
      cursor,
      bgcolor: "background.paper",
      "&:hover": { bgcolor: hoverBg },
      "&.Mui-selected": {
        bgcolor: hoverBg,
        "&:hover": { bgcolor: hoverBg },
      },
    },
    [`& .${gridClasses.footerContainer}`]: {
      borderTop: "none",
    },
  };
}

function DataGridFooter({ rowCount }) {
  return function Footer() {
    return (
      <GridFooterContainer
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          minHeight: 48,
          px: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Total Rows: {rowCount}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <GridPagination />
      </GridFooterContainer>
    );
  };
}

export function DataGridPage({ children, className = "" }) {
  return (
    <Box className={`page-fill fade-up ${className}`.trim()}>
      {children}
    </Box>
  );
}

export function DataGridCard({ title, headerRight, children, fillHeight = true, sx }) {
  return (
    <Box
      sx={{
        ...gridShellSx,
        ...(fillHeight ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 } : {}),
        ...sx,
      }}
    >
      {(title || headerRight) && (
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          {title && (
            <Typography variant="h6" fontWeight={600} color="text.primary">
              {title}
            </Typography>
          )}
          {headerRight}
        </Box>
      )}
      {fillHeight ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {children}
        </Box>
      ) : children}
    </Box>
  );
}

export default function AppDataGrid({
  rows,
  columns,
  onRowClick,
  pageSize = 25,
  minHeight = 400,
  autoHeight = false,
  fillHeight = true,
  hideFooter = false,
  cursor = "pointer",
  sx: sxProp,
}) {
  const gridSx = useDataGridSx({
    cursor: onRowClick ? cursor : "default",
    fillHeight,
  });

  const grid = (
    <DataGrid
      rows={rows}
      columns={columns}
      rowHeight={48}
      columnHeaderHeight={44}
      disableRowSelectionOnClick
      pagination={!hideFooter && rows.length > pageSize}
      pageSizeOptions={[10, 25, 50, 100]}
      initialState={{
        pagination: { paginationModel: { pageSize, page: 0 } },
      }}
      onRowClick={onRowClick ? ({ row }) => onRowClick(row) : undefined}
      slots={hideFooter ? {} : { footer: DataGridFooter(rows.length) }}
      autoHeight={!fillHeight && autoHeight}
      sx={{
        ...gridSx,
        ...(fillHeight
          ? { flex: 1, minHeight: 0, height: "100%" }
          : { minHeight: autoHeight ? undefined : minHeight }),
        ...sxProp,
      }}
    />
  );

  if (!fillHeight) return grid;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", width: "100%" }}>
      {grid}
    </Box>
  );
}

export function useAttendanceColumns({ onEmployeeClick, includeRole = false } = {}) {
  return useMemo(() => {
    const cols = [
      {
        field: "name",
        headerName: "Employee",
        flex: 1.2,
        minWidth: 220,
        renderCell: ({ row }) => (
          <PersonCell name={row.name} subtitle={includeRole ? row.role : undefined} />
        ),
      },
      {
        field: "ci",
        headerName: "Clock in",
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => <TextCell value={row.ci || row.clockIn} />,
      },
    ];
    if (!includeRole) {
      cols.push({
        field: "dept",
        headerName: "Department",
        flex: 0.75,
        minWidth: 120,
        renderCell: ({ value }) => <TextCell value={value} />,
      });
    }
    cols.push({
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 115,
      renderCell: ({ value }) => <GridStatusPill status={value} />,
    });
    return cols;
  }, [includeRole]);
}

export function useHistoryColumns() {
  return useMemo(
    () => [
      {
        field: "date",
        headerName: "Day",
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) => <TextCell value={value} fontWeight={500} />,
      },
      {
        field: "clockIn",
        headerName: "Clock in",
        flex: 0.7,
        minWidth: 100,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: "clockOut",
        headerName: "Clock out",
        flex: 0.7,
        minWidth: 100,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 115,
        renderCell: ({ value }) => <GridStatusPill status={value} />,
      },
    ],
    []
  );
}
